import { useMutation } from '@apollo/client/react';
import { Button, Dropdown, Switch, Tag, message } from 'antd';
import clsx from 'clsx';
import { filter, map } from 'lodash';
import moment from 'moment-timezone';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../AppContext';
import {
  AddButton,
  DeleteButton,
  DuplicateIcon,
  EditButton,
  KebabMenu,
  Timezone,
} from '../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  BREAKPOINTS,
  BULK_ACTION,
  BULK_JOB_STATUS,
  DEFAULTDATETIMEFORMAT,
  DEFAULT_PAGE_SIZE,
  DESELECT_MESSAGE,
  GA_EVENT,
  GA_LABEL,
  ROUTES,
  SYSTEM_ROLES,
  UOMS,
} from '../../../common/constants';
import { Event } from '../../../common/trackEvents';
import useRouter from '../../../common/useRouter';
import { removeHistoryStateData } from '../../../common/utils';
import CanPerform from '../../../components/CanPerform';
import CommonCard from '../../../components/CommonCard';
import CommonSelectionBar from '../../../components/CommonSelectionBar';
import CommonTable from '../../../components/CommonTable';
import CommonTooltip from '../../../components/CommonTooltip';
import CustomCheckboxSelection from '../../../components/CustomCheckboxSelection';
import '../../../components/CustomCheckboxSelection.less';
import InfiniteScrollHandler from '../../../components/InfiniteScrollHandler';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import ActivationModal from '../ActivationModal';
import AddEditEqcModal from '../AddEditEqcModal';
import DeleteModal from '../DeleteModal';
import DuplicateModal from '../DuplicateModal';
import {
  BULK_DELETE_EQC_TYPES,
  BULK_SET_EQC_TYPES_STATUS,
  DELETE_EQC_TYPE,
  UPDATE_EQC_TYPE_STATUS,
} from '../graphql/Mutation';
import { GET_BULK_EQC_TYPE_JOB_STATUS, GET_EQC_TYPE } from '../graphql/Queries';
import BulkChecklistConfirmationModal from './BulkChecklistConfirmationModal';
import BulkExportModal from './BulkExportModal';

const EqcTable = () => {
  const {
    getCurrentUser,
    state: { currentRole },
  } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const { navigate, location } = useRouter();
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const navSelectedRowKeys = location?.state?.navSelectedRowKeys;
  const navSelectedRows = location?.state?.navSelectedRows;
  const navIsSelectAllAcrossPages = location?.state?.navIsSelectAllAcrossPages;
  const navExceptIds = location?.state?.navExceptIds;

  const initialEqcFilter = {
    skip: 0,
    limit: 10,
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const [paginationProp, setPaginationProp] = useState(
    navPagination || initialPaginationValue,
  );
  const [showStatusModal, setStatusModal] = useState(false);
  const [showDuplicateModal, setDuplicateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [eqcData, setEqcData] = useState();
  const [isActive, setIsActive] = useState(false);
  const [mutationId, setMutationId] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [title, setTitle] = useState('');
  const [eqcFilter, setEqcFilter] = useState(navFilter || initialEqcFilter);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [eqcTypeListData, setEqcTypeListData] = useState([]);
  const [eqcTypeTotalCount, setEqcTypeTotalCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkExportModalVisible, setBulkExportModalVisible] = useState(false);
  const [bulkExportLoading, setBulkExportLoading] = useState(false);
  const [isSelectAllAcrossPages, setIsSelectAllAcrossPages] = useState(
    navIsSelectAllAcrossPages || false,
  );
  const [exceptIds, setExceptIds] = useState(navExceptIds || []);
  const [selectAllTotal, setSelectAllTotal] = useState(0);
  const bulkJobsRef = useRef(new Map());
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isAnyRowSelected = selectedRows?.length > 0 || isSelectAllAcrossPages;

  useEffect(() => {
    return () => {
      bulkJobsRef.current?.forEach((jobInfo) => {
        if (jobInfo?.timeoutId) {
          clearTimeout(jobInfo.timeoutId);
        }
      });
      bulkJobsRef.current?.clear();
    };
  }, []);

  const startJobTracking = useCallback((jobId, action) => {
    if (!jobId || !action) return;
    bulkJobsRef.current?.set(jobId, { action, timeoutId: null });
  }, []);

  const clearJobTracking = useCallback((jobId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo?.timeoutId) {
      clearTimeout(jobInfo.timeoutId);
    }
    bulkJobsRef.current?.delete(jobId);
  }, []);

  const updateJobTimeout = useCallback((jobId, timeoutId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo) {
      bulkJobsRef.current?.set(jobId, { ...jobInfo, timeoutId });
    }
  }, []);

  const getJobAction = useCallback((jobId) => {
    if (!jobId) return null;
    return bulkJobsRef.current?.get(jobId)?.action || null;
  }, []);

  useEffect(() => {
    if (navFilter) {
      setEqcFilter(navFilter);
    }
    if (navPagination) {
      setPaginationProp(navPagination);
    }
    if (navSelectedRowKeys) {
      setSelectedRowKeys(navSelectedRowKeys);
    }
    if (navSelectedRows) {
      setSelectedRows(navSelectedRows);
    }
    if (navIsSelectAllAcrossPages) {
      setIsSelectAllAcrossPages(navIsSelectAllAcrossPages);
      if (navPagination?.total) {
        setSelectAllTotal(navPagination.total);
      }
    }
    if (navExceptIds) {
      setExceptIds(navExceptIds);
    }
  }, [
    navFilter,
    navPagination,
    navSelectedRowKeys,
    navSelectedRows,
    navIsSelectAllAcrossPages,
    navExceptIds,
  ]);

  const [fetchEqcData, { loading }] = useLazyQueryWithCallbacks(GET_EQC_TYPE, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const data = res?.eqcTypeList?.data;
      const total = res?.eqcTypeList?.total;
      setEqcTypeTotalCount(total);
      const pagination = {
        ...paginationProp,
        total: res?.eqcTypeList?.total,
      };
      if (scrollFlag) {
        const datalist = [...eqcTypeListData, ...data];
        setEqcTypeListData(datalist);
        setScrollFlag(false);
      } else {
        const datalist = [...data];
        setEqcTypeListData(datalist);

        // Handle cross-page selection for newly loaded pages
        if (isSelectAllAcrossPages) {
          // Add all new items that aren't already in selectedRows and not in exceptIds
          const existingIds = selectedRows?.map((row) => row?.id) || [];
          const newItemsToAdd =
            datalist?.filter(
              (item) =>
                !existingIds?.includes(item?.id) &&
                !exceptIds?.includes(item?.id),
            ) || [];

          const updatedSelectedRows = [...selectedRows, ...newItemsToAdd];
          const updatedSelectedKeys =
            updatedSelectedRows
              ?.map((row) => row?.id)
              ?.filter((id) => !exceptIds?.includes(id)) || [];

          setSelectedRows(updatedSelectedRows);
          setSelectedRowKeys(updatedSelectedKeys);
        }
        // Note: selectedRows restoration is handled by the useEffect that watches eqcTypeListData
        // This ensures selections are restored properly when data loads
      }
      setHasMore(!!data?.length);
      setPaginationProp(pagination);
    },
    onError() {},
  });

  const [updateStatus] = useMutation(UPDATE_EQC_TYPE_STATUS, {
    onCompleted() {
      Event(GA_EVENT.EDIT_GLOBAL_CHECKLIST, {
        label: GA_LABEL.EDIT_GLOBAL_CHECKLIST,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        checklist_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setStatusModal(false);
      fetchEqcData({ variables: { filter: eqcFilter } });
    },
    onError() {},
  });
  const handleRefetchAfterDelete = () => {
    const newSkip =
      eqcTypeListData?.length === 1
        ? Math.max(0, eqcFilter?.skip - paginationProp?.pageSize)
        : eqcFilter?.skip;
    setEqcFilter({
      ...eqcFilter,
      skip: newSkip,
    });
    fetchEqcData({
      variables: {
        filter: {
          ...eqcFilter,
          skip: newSkip,
        },
      },
    });
  };
  // Bulk operations handlers - defined early to avoid use-before-define errors
  const handleClearBulkSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
    setSelectAllTotal(0);
    // Silently clear selection from browser history to prevent persistence on page refresh
    // Using window.history.replaceState to avoid triggering React Router re-renders and API calls
    if (location?.state) {
      const cleanedState = { ...location?.state };
      delete cleanedState.navSelectedRowKeys;
      delete cleanedState.navSelectedRows;
      delete cleanedState.navIsSelectAllAcrossPages;
      delete cleanedState.navExceptIds;
      const newUsrState =
        Object.keys(cleanedState)?.length > 0 ? cleanedState : undefined;
      // eslint-disable-next-line no-undef
      const currentHistoryState = window.history?.state || {};
      // eslint-disable-next-line no-undef
      window.history.replaceState(
        { ...currentHistoryState, usr: newUsrState },
        '',
      );
    }
  }, [location]);

  const [getBulkJobStatus] = useLazyQueryWithCallbacks(
    GET_BULK_EQC_TYPE_JOB_STATUS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const jobId = res?.getBulkEqcTypeJobStatus?.jobId;
        const state = res?.getBulkEqcTypeJobStatus?.state;

        if (!jobId) return;

        const currentAction = getJobAction(jobId);

        if (state === BULK_JOB_STATUS.PROCESSING) {
          const timeoutId = setTimeout(() => {
            getBulkJobStatus({
              variables: { jobId },
            });
          }, 3000);
          updateJobTimeout(jobId, timeoutId);
        }
        if (
          state === BULK_JOB_STATUS.COMPLETED ||
          state === BULK_JOB_STATUS.FAILED
        ) {
          clearJobTracking(jobId);

          if (state === BULK_JOB_STATUS.COMPLETED) {
            if (
              currentAction === BULK_ACTION.DEACTIVATE ||
              currentAction === BULK_ACTION.DELETE
            ) {
              handleRefetchAfterDelete();
            } else {
              fetchEqcData({ variables: { filter: eqcFilter } });
            }
          }
        }
      },
    },
  );

  const [deleteEqc] = useMutation(DELETE_EQC_TYPE, {
    onCompleted() {
      Event(GA_EVENT.DELETE_GLOBAL_CHECKLIST, {
        label: GA_LABEL.DELETE_GLOBAL_CHECKLIST,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        checklist_id: mutationId,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      handleRefetchAfterDelete();
    },
    onError() {},
  });

  const [bulkSetEqcTypesStatus] = useMutation(BULK_SET_EQC_TYPES_STATUS, {
    onCompleted(response) {
      const { status, jobId } = response?.bulkSetEqcTypesStatus || {};

      if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
        startJobTracking(jobId, bulkAction);

        getBulkJobStatus({
          variables: { jobId },
        });

        handleClearBulkSelection();
        setBulkModalVisible(false);
        setBulkAction(null);
      }
    },
    onError() {},
  });

  const [bulkDeleteEqcTypes] = useMutation(BULK_DELETE_EQC_TYPES, {
    onCompleted(response) {
      const { status, jobId } = response?.bulkDeleteEqcTypes || {};

      if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
        startJobTracking(jobId, BULK_ACTION.DELETE);

        getBulkJobStatus({
          variables: { jobId },
        });

        handleClearBulkSelection();
        setBulkModalVisible(false);
        setBulkAction(null);
      }
    },
    onError() {},
  });

  useEffect(() => {
    fetchEqcData({ variables: { filter: eqcFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eqcFilter]);

  // Restore selections when data changes - show which items on current page are selected
  useEffect(() => {
    if (selectedRows?.length > 0 && eqcTypeListData?.length > 0) {
      const currentPageIds = eqcTypeListData?.map((item) => item?.id) || [];
      const selectedIdsOnCurrentPage =
        selectedRows
          ?.filter(
            (row) =>
              currentPageIds?.includes(row?.id) &&
              !(isSelectAllAcrossPages && exceptIds?.includes(row?.id)),
          )
          ?.map((row) => row?.id) || [];

      // Always update selectedRowKeys to show current page selections for table component
      setSelectedRowKeys(selectedIdsOnCurrentPage);
    } else if (selectedRows?.length === 0 && eqcTypeListData?.length > 0) {
      // Clear selectedRowKeys if no selections exist
      setSelectedRowKeys([]);
    }
  }, [eqcTypeListData, selectedRows, isSelectAllAcrossPages, exceptIds]);

  // Auto-clear selection when all items have been excluded
  useEffect(() => {
    if (
      isSelectAllAcrossPages &&
      exceptIds?.length >= (selectAllTotal || 0) &&
      selectAllTotal > 0
    ) {
      handleClearBulkSelection();
    }
  }, [
    exceptIds,
    isSelectAllAcrossPages,
    selectAllTotal,
    handleClearBulkSelection,
  ]);

  const refetchEqcDetails = () => {
    fetchEqcData({
      variables: {
        filter: { ...eqcFilter, sortBy: { field: 'createdAt', order: 'DESC' } },
      },
    });
  };

  // Row selection handlers
  const handleRowSelectionChange = (selectedKeys, selectedRowsData) => {
    if (isSelectAllAcrossPages) {
      const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];
      const newlyDeselected =
        currentPageKeys?.filter((key) => !selectedKeys?.includes(key)) || [];
      const reSelected =
        selectedKeys?.filter((key) => exceptIds?.includes(key)) || [];
      setExceptIds((prev) => {
        let updated = [...prev];
        newlyDeselected?.forEach((id) => {
          if (!updated?.includes(id)) updated.push(id);
        });
        updated = updated?.filter((id) => !reSelected?.includes(id));
        return updated;
      });
      setSelectedRowKeys(selectedKeys);
      return;
    }

    // Get current page IDs
    const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];

    // Remove current page items from existing selectedRows first (to avoid duplicates)
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];

    // Add the new selected rows from current page (full data objects)
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...selectedRowsData,
    ];

    // Update selectedRowKeys to match (just IDs for table component)
    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectRow = (record, selected) => {
    const recordId = record?.id;

    if (selected) {
      if (isSelectAllAcrossPages) {
        // Re-selecting a previously excluded item
        setExceptIds((prev) => prev?.filter((id) => id !== recordId) || []);
        setSelectedRowKeys((prev) =>
          prev?.includes(recordId) ? prev : [...(prev || []), recordId],
        );
        return;
      }
      // Add to selectedRows if not already included
      const isAlreadySelected = selectedRows?.some(
        (row) => row?.id === recordId,
      );
      const newSelectedRows = isAlreadySelected
        ? selectedRows
        : [...selectedRows, record];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];

      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    } else {
      if (isSelectAllAcrossPages) {
        // Keep cross-page mode ON; track excluded ID
        setExceptIds((prev) =>
          prev?.includes(recordId) ? prev : [...(prev || []), recordId],
        );
        const remainingKeys =
          selectedRowKeys?.filter((key) => key !== recordId) || [];
        setSelectedRowKeys(remainingKeys);
        return;
      }

      const newSelectedRows =
        selectedRows?.filter((row) => row?.id !== recordId) || [];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];

      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
    }
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      if (isSelectAllAcrossPages) {
        // Re-select all on current page: remove them from exceptIds
        const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];
        setExceptIds(
          (prev) => prev?.filter((id) => !currentPageKeys?.includes(id)) || [],
        );
        setSelectedRowKeys(currentPageKeys);
        return;
      }
      const allKeys = eqcTypeListData?.map((item) => item?.id) || [];
      setSelectedRowKeys(allKeys);
      setSelectedRows(eqcTypeListData || []);
    } else {
      if (isSelectAllAcrossPages) {
        // Add all current page items to exceptIds
        const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];
        setExceptIds((prev) => {
          const updated = [...prev];
          currentPageKeys?.forEach((id) => {
            if (!updated?.includes(id)) updated.push(id);
          });
          return updated;
        });
        setSelectedRowKeys([]);
        return;
      }
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  // Custom selection handlers
  const handleSelectAllOnPage = () => {
    const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
    setSelectAllTotal(0);

    // Remove current page items from selectedRows first, then add current page items
    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];

    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...(eqcTypeListData || []),
    ];

    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectAllAcrossPages = async () => {
    try {
      // Mark cross-page selection mode
      setExceptIds([]);
      setIsSelectAllAcrossPages(true);
      setSelectAllTotal(paginationProp?.total || 0);

      // Select current page items for immediate visual feedback
      const currentPageKeys = eqcTypeListData?.map((item) => item?.id) || [];
      setSelectedRowKeys(currentPageKeys);
      setSelectedRows(eqcTypeListData || []);
    } catch (error) {
      message.error('Failed to select all items across pages');
      return error;
    }
  };

  const handleBulkActivate = () => {
    setBulkAction(BULK_ACTION.ACTIVATE);
    setBulkModalVisible(true);
  };

  const handleBulkDeactivate = () => {
    setBulkAction(BULK_ACTION.DEACTIVATE);
    setBulkModalVisible(true);
  };

  const handleBulkDelete = () => {
    setBulkAction(BULK_ACTION.DELETE);
    setBulkModalVisible(true);
  };
  //! for future use
  // const handleBulkExport = () => {
  //   setBulkExportModalVisible(true);
  // };

  const handleBulkConfirm = async () => {
    if (!selectedRowKeys?.length && !isSelectAllAcrossPages) {
      message.warning('No items selected');
      return;
    }

    setBulkLoading(true);

    try {
      if (bulkAction === BULK_ACTION.DELETE) {
        // Handle bulk deletion using new mutation
        const mutationVariables = {
          isAllSelected: isSelectAllAcrossPages,
        };

        if (!isSelectAllAcrossPages) {
          mutationVariables.eqcTypeId =
            selectedRows?.map((row) => row?.id) || [];
        }
        if (isSelectAllAcrossPages && exceptIds?.length > 0) {
          mutationVariables.exceptEqcTypeId = exceptIds;
        }

        await bulkDeleteEqcTypes({
          variables: mutationVariables,
        });
      } else if (
        bulkAction === BULK_ACTION.ACTIVATE ||
        bulkAction === BULK_ACTION.DEACTIVATE
      ) {
        // Handle bulk activate/deactivate using new mutation
        const targetActiveStatus = bulkAction === BULK_ACTION.ACTIVATE;

        // Prepare mutation variables
        const mutationData = {
          isActive: targetActiveStatus,
          isAllSelected: isSelectAllAcrossPages,
        };

        // Add eqcTypeId array only if not selecting all across pages
        if (!isSelectAllAcrossPages) {
          mutationData.eqcTypeId = selectedRows?.map((row) => row?.id) || [];
        }
        if (isSelectAllAcrossPages && exceptIds?.length > 0) {
          mutationData.exceptEqcTypeId = exceptIds;
        }

        await bulkSetEqcTypesStatus({
          variables: {
            data: mutationData,
          },
        });
      }
    } catch (error) {
      return error;
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkCancel = () => {
    setBulkModalVisible(false);
    setBulkAction(null);
  };

  const handleBulkExportConfirm = async () => {
    if (!selectedRowKeys?.length && !isSelectAllAcrossPages) {
      message.warning('No items selected');
      return;
    }

    setBulkExportLoading(true);

    try {
      // TODO: Add bulk export API call here
      // This will be implemented based on backend API requirements
      message.success(
        'Bulk export initiated. You will receive an email with the download link.',
      );

      setBulkExportModalVisible(false);
      handleClearBulkSelection();
    } catch (error) {
      message.error('Failed to initiate bulk export');
    } finally {
      setBulkExportLoading(false);
    }
  };

  const handleBulkExportCancel = () => {
    setBulkExportModalVisible(false);
  };

  const handleAddEditEqc = () => {
    setShowModal(true);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });

    // Only clear current page visual selections (selectedRowKeys), keep selectedRows intact
    // selectedRows contains all cross-page data and should never be cleared on pagination
    if (!isSelectAllAcrossPages) {
      setSelectedRowKeys([]);
    }

    const newFilter = {
      ...eqcFilter,
      skip,
      limit: pagination.pageSize,
      sortBy: sorter?.column
        ? {
            field: sorter.columnKey,
            order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
          }
        : { field: 'createdAt', order: 'DESC' },
    };

    setEqcFilter(newFilter);
    fetchEqcData({
      variables: {
        filter: newFilter,
      },
    });
  };

  const ActiveStatus = ({ record }) => {
    if (record?.isActive === true) {
      return <Tag className="active-tag">Active</Tag>;
    }
    return <Tag className="inactive-tag">Inactive</Tag>;
  };

  const handleActiveStatus = (record) => {
    if (record?.isActive === false) setTitle('Activate');
    else setTitle('Deactivate');
    setIsActive(record?.isActive);
    setStatusModal(true);
  };
  const getActionButtons = (record, index) => {
    const statusLabel = record?.isActive ? 'Deactivate' : 'Activate';
    const statusTooltipTitle = isAnyRowSelected
      ? DESELECT_MESSAGE
      : statusLabel;

    const handleEdit = (e) => {
      e?.stopPropagation?.();
      setEqcData(record);
      handleAddEditEqc();
    };
    const handleDuplicate = (e) => {
      e?.stopPropagation?.();
      setEqcData(record);
      setDuplicateModal(true);
    };
    const handleDelete = (e) => {
      e?.stopPropagation?.();
      setTitle(record?.name);
      setMutationId(record?.id);
      setDeleteModal(true);
    };
    const handleSwitch = (e) => {
      e?.stopPropagation?.();
      setMutationId(record?.id);
      handleActiveStatus(record);
    };
    const menu = [
      {
        key: 'edit',
        label: (
          <div id="eqc-edit" onClick={handleEdit}>
            Edit
          </div>
        ),
      },
      {
        key: 'duplicate',
        label: (
          <div id="eqc-duplicate" onClick={handleDuplicate}>
            Duplicate
          </div>
        ),
      },
      {
        key: 'delete',
        label: (
          <span id="eqc-delete" onClick={handleDelete} className="text-danger">
            Delete
          </span>
        ),
      },
    ];

    return isDesktopViewport ? (
      <div
        id={`action-buttons-desktop-${index}`}
        className="d-flex align-center"
      >
        <CommonTooltip
          id={`edit-tooltip-${index}`}
          title={isAnyRowSelected ? DESELECT_MESSAGE : 'Edit'}
        >
          <Button
            id={`edit-btn-${index}`}
            shape="circle"
            icon={<EditButton />}
            className={clsx(
              'edit-button pointer b-0',
              isAnyRowSelected && 'disabled-button',
            )}
            onClick={handleEdit}
            disabled={isAnyRowSelected}
          />
        </CommonTooltip>
        <CanPerform action={ALLOWED_ACTION_KEYS.DELETE_EQC}>
          <CommonTooltip
            id={`duplicate-tooltip-${index}`}
            title={isAnyRowSelected ? DESELECT_MESSAGE : 'Duplicate'}
          >
            <Button
              id={`duplicate-btn-${index}`}
              shape="circle"
              icon={<DuplicateIcon />}
              className={clsx(
                'pointer b-0',
                isAnyRowSelected && 'disabled-button',
              )}
              onClick={handleDuplicate}
              disabled={isAnyRowSelected}
            />
          </CommonTooltip>
          <CommonTooltip
            id={`delete-tooltip-${index}`}
            title={isAnyRowSelected ? DESELECT_MESSAGE : 'Delete'}
          >
            <Button
              id={`delete-btn-${index}`}
              shape="circle"
              icon={<DeleteButton />}
              className={clsx(
                'edit-button pointer b-0',
                isAnyRowSelected && 'disabled-button',
              )}
              onClick={handleDelete}
              disabled={isAnyRowSelected}
            />
          </CommonTooltip>
          <CommonTooltip
            id={`status-tooltip-${index}`}
            title={statusTooltipTitle}
          >
            <Switch
              id={`status-switch-${index}`}
              onChange={(_, e) => handleSwitch(e)}
              checked={record?.isActive}
              disabled={isAnyRowSelected}
            />
          </CommonTooltip>
        </CanPerform>
      </div>
    ) : (
      <div
        id={`action-buttons-mobile-${index}`}
        className="d-flex align-center"
      >
        <CommonTooltip
          id={`mobile-status-tooltip-${index}`}
          trigger="focus"
          title={statusTooltipTitle}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        >
          <Switch
            id={`mobile-status-switch-${index}`}
            onChange={(_, e) => handleSwitch(e)}
            checked={record?.isActive}
            size="small"
            disabled={isAnyRowSelected}
          />
        </CommonTooltip>
        <Dropdown
          id={`kebab-dropdown-${index}`}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          menu={{ items: menu }}
          trigger={['click']}
        >
          <KebabMenu id={`kebab-menu-${index}`} />
        </Dropdown>
      </div>
    );
  };
  const columns = [
    {
      title: '#',
      key: 'id',
      render: (_, __, index) => {
        return <div>{eqcFilter?.skip + index + 1}</div>;
      },
    },
    {
      title: 'CHECKLIST',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'uom' && sortedInfo?.order,
    },
    {
      title: 'REFERENCE NUMBER',
      dataIndex: 'iso',
      key: 'iso',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'iso' && sortedInfo?.order,
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'isActive' && sortedInfo?.order,
      render: (_, record) => {
        return <ActiveStatus record={record} />;
      },
    },
    {
      title: 'UPDATED BY',
      key: 'updatedBy',
      render: (record) => {
        return record?.creator?.name;
      },
    },
    {
      title: 'UPDATED AT',
      key: 'updatedAt',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'updatedAt' && sortedInfo?.order,
      render: (_, record) => {
        return record?.updatedAt ? (
          <div>
            {moment(`${record?.updatedAt}`)
              ?.tz(currentUser?.tenantUser?.tenant?.timeZone)
              ?.format(DEFAULTDATETIMEFORMAT)}
            <div className="d-flex align-center timezone-div">
              <Timezone title="timeZone" />
              {currentUser?.tenantUser?.tenant?.timeZone}
            </div>
          </div>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'ACTIONS',
      render: (record, _, index) => getActionButtons(record, index),
    },
  ];

  const onSearchChange = async (value) => {
    if (!value) {
      removeHistoryStateData(navigate, location, 'navFilter', 'search');
    }
    setEqcFilter({ ...eqcFilter, skip: 0, search: value });
    setPaginationProp(initialPaginationValue);
    fetchEqcData({
      variables: { filter: { ...eqcFilter, skip: 0, search: value } },
    });
  };

  const handleRefetch = () => {
    fetchEqcData({
      variables: {
        filter: {
          ...eqcFilter,
          skip: eqcTypeListData?.length,
        },
      },
    });
  };
  const handleRowClick = (record) => {
    if (record?.id) {
      navigate(`${ROUTES.SETUP}${ROUTES.CHECKLISTS}/${record?.id}`, {
        state: {
          navFilter: eqcFilter,
          navPagination: paginationProp,
          navSelectedRowKeys: selectedRowKeys,
          navSelectedRows: selectedRows,
          navIsSelectAllAcrossPages: isSelectAllAcrossPages,
          navExceptIds: exceptIds,
          currentPath: location?.pathname,
        },
      });
    }
  };

  // Row selection configuration for System Admins only
  const getRowSelection = () => {
    if (currentRole !== SYSTEM_ROLES.SYSTEM_ADMIN || !isDesktopViewport) {
      return undefined;
    }

    return {
      selectedRowKeys,
      onChange: handleRowSelectionChange,
      onSelect: handleSelectRow,
      onSelectAll: handleSelectAll,
      getCheckboxProps: (record) => ({
        disabled: false,
        name: record?.name,
      }),
      columnTitle: (
        <CustomCheckboxSelection
          checked={
            selectedRowKeys?.length === eqcTypeListData?.length &&
            eqcTypeListData?.length > 0
          }
          indeterminate={
            selectedRowKeys?.length > 0 &&
            selectedRowKeys?.length < eqcTypeListData?.length
          }
          disabled={!eqcTypeListData?.length}
          onSelectAllPage={handleSelectAllOnPage}
          onSelectAllAcross={handleSelectAllAcrossPages}
          onDeselectAll={handleClearBulkSelection}
          currentPageCount={eqcTypeListData?.length}
          totalCount={paginationProp?.total}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          selectedCount={
            isSelectAllAcrossPages
              ? (selectAllTotal || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
        />
      ),
    };
  };

  return (
    <>
      {showModal && (
        <AddEditEqcModal
          showModal={showModal}
          setShowModal={setShowModal}
          setEqcData={setEqcData}
          eqcData={eqcData}
          isUpdate={!!eqcData}
          refetchUserDataWithInitialValues={refetchEqcDetails}
        />
      )}

      {/* Bulk Operations Modal */}
      {bulkModalVisible && (
        <BulkChecklistConfirmationModal
          visible={bulkModalVisible}
          onCancel={handleBulkCancel}
          onConfirm={handleBulkConfirm}
          action={bulkAction}
          count={
            isSelectAllAcrossPages
              ? (selectAllTotal || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          loading={bulkLoading}
        />
      )}

      {/* Bulk Export Modal */}
      {bulkExportModalVisible && (
        <BulkExportModal
          visible={bulkExportModalVisible}
          onCancel={handleBulkExportCancel}
          onConfirm={handleBulkExportConfirm}
          count={
            isSelectAllAcrossPages
              ? (selectAllTotal || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          loading={bulkExportLoading}
        />
      )}
      {showDeleteModal && (
        <DeleteModal
          showModal={showDeleteModal}
          setShowModal={setDeleteModal}
          title={title}
          deleteEqc={deleteEqc}
          mutationId={mutationId}
          setMutationId={setMutationId}
        />
      )}
      {showDuplicateModal && (
        <DuplicateModal
          showModal={showDuplicateModal}
          setShowModal={setDuplicateModal}
          eqcData={eqcData}
          setEqcData={setEqcData}
          refetchEqcDataWithInitialValues={refetchEqcDetails}
        />
      )}
      {showStatusModal && (
        <ActivationModal
          showModal={showStatusModal}
          setShowModal={setStatusModal}
          title={title}
          updateStatus={updateStatus}
          mutationId={mutationId}
          setMutationId={setMutationId}
          isActive={isActive}
          name="Checklist"
          subtitle="Checklist"
        />
      )}
      <Portal portalId="sub-header">
        <h5
          className={clsx('mt-5', !isDesktopViewport ? 'ml-16' : '')}
        >{`${eqcTypeTotalCount} ${eqcTypeTotalCount > 1 ? `Checklists` : `Checklist`}`}</h5>
      </Portal>
      <Portal portalId="eqc-add-search-button">
        <SearchComponent
          id="search-container-id"
          getData={onSearchChange}
          className="search-component mr-10"
          defaultValue={navFilter?.search}
        />
        <CommonTooltip title={isAnyRowSelected ? DESELECT_MESSAGE : ''}>
          <Button
            className="d-flex"
            shape="round"
            type="primary"
            id="add-btn"
            icon={<AddButton />}
            onClick={handleAddEditEqc}
            disabled={isAnyRowSelected}
          >
            {isDesktopViewport && 'Add'}
          </Button>
        </CommonTooltip>
      </Portal>

      {/* Professional Selection Bar beside Checklists header using Portal */}
      {isDesktopViewport &&
        currentRole === SYSTEM_ROLES.SYSTEM_ADMIN &&
        isAnyRowSelected && (
          <Portal portalId="eqc-selection-bar">
            <CommonSelectionBar
              selectedCount={
                isSelectAllAcrossPages
                  ? (selectAllTotal || 0) - (exceptIds?.length || 0)
                  : selectedRows?.length || 0
              }
              className="ml-10"
              onClear={handleClearBulkSelection}
            >
              {(() => {
                // For cross-page selection, show both buttons since we don't know exact status breakdown
                if (isSelectAllAcrossPages) {
                  return (
                    <>
                      <CommonTooltip trigger="hover" title="Activate Selected">
                        <Button
                          type="primary"
                          shape="round"
                          className="ml-5"
                          loading={bulkLoading}
                          onClick={handleBulkActivate}
                        >
                          Activate
                        </Button>
                      </CommonTooltip>
                      <CommonTooltip
                        trigger="hover"
                        title="Deactivate Selected"
                      >
                        <Button
                          type="primary"
                          shape="round"
                          className="ml-5"
                          loading={bulkLoading}
                          onClick={handleBulkDeactivate}
                        >
                          Deactivate
                        </Button>
                      </CommonTooltip>
                    </>
                  );
                }

                // For regular selection, show buttons based on actual selection state
                const inactiveCount =
                  filter(selectedRows, (item) => !item?.isActive)?.length || 0;
                const activeCount =
                  filter(selectedRows, (item) => item?.isActive)?.length || 0;
                const totalSelected = selectedRows?.length || 0;

                // Only inactive items selected - show only Activate button
                if (inactiveCount === totalSelected && inactiveCount > 0) {
                  return (
                    <CommonTooltip trigger="hover" title="Activate Selected">
                      <Button
                        type="primary"
                        shape="round"
                        className="ml-5"
                        loading={bulkLoading}
                        onClick={handleBulkActivate}
                      >
                        Activate
                      </Button>
                    </CommonTooltip>
                  );
                }

                // Only active items selected - show only Deactivate button
                if (activeCount === totalSelected && activeCount > 0) {
                  return (
                    <CommonTooltip trigger="hover" title="Deactivate Selected">
                      <Button
                        type="primary"
                        shape="round"
                        className="ml-5"
                        loading={bulkLoading}
                        onClick={handleBulkDeactivate}
                      >
                        Deactivate
                      </Button>
                    </CommonTooltip>
                  );
                }

                // Mixed selection - show both buttons
                if (inactiveCount > 0 && activeCount > 0) {
                  return (
                    <>
                      <CommonTooltip trigger="hover" title="Activate Selected">
                        <Button
                          type="primary"
                          shape="round"
                          className="ml-5"
                          loading={bulkLoading}
                          onClick={handleBulkActivate}
                        >
                          Activate
                        </Button>
                      </CommonTooltip>
                      <CommonTooltip
                        trigger="hover"
                        title="Deactivate Selected"
                      >
                        <Button
                          type="primary"
                          shape="round"
                          className="ml-5"
                          loading={bulkLoading}
                          onClick={handleBulkDeactivate}
                        >
                          Deactivate
                        </Button>
                      </CommonTooltip>
                    </>
                  );
                }

                return null;
              })()}

              {/* <CommonTooltip trigger="hover" title="Export Selected">
                <Button
                  className="delete-button pointer b-0 ml-5"
                  shape="round"
                  icon={<Export height="20px" />}
                  loading={bulkExportLoading}
                  onClick={handleBulkExport}
                />
              </CommonTooltip> */}

              <CommonTooltip trigger="hover" title="Delete Selected">
                <Button
                  className="delete-button pointer b-0 ml-5"
                  shape="round"
                  icon={<DeleteButton />}
                  loading={bulkLoading}
                  onClick={handleBulkDelete}
                />
              </CommonTooltip>
            </CommonSelectionBar>
          </Portal>
        )}

      {isDesktopViewport ? (
        <div id="eqc-desktop-table" className="pointer">
          <CommonTable
            id="eqc-common-table"
            loadingData={loading}
            columns={columns}
            data={eqcTypeListData || []}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(obj) => obj?.id ?? obj?.key}
            rowSelection={getRowSelection()}
            rowClassName={(record) => {
              const classes = ['eqc-table-row'];
              if (!record?.isActive) classes.push('deactivated-color');
              return classes.join(' ');
            }}
            onRow={(record) => {
              return {
                onClick: (event) => {
                  if (event?.target?.closest('.ant-checkbox-wrapper')) {
                    return;
                  }
                  handleRowClick(record);
                },
              };
            }}
          />
        </div>
      ) : (
        <InfiniteScrollHandler
          id="eqc-scroll-handler"
          scrollFlag={scrollFlag}
          loading={loading}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          wrapperClassName="eqc-type-scroll-wrapper"
          dataLength={eqcTypeListData?.length}
          skeletonRows={columns?.length - 2}
        >
          {map(eqcTypeListData, (eqcType, index) => {
            return (
              <CommonCard
                id={`eqc-card-${index}`}
                key={eqcType?.id}
                onClick={() => handleRowClick(eqcType)}
              >
                <div
                  id={`eqc-card-content-${index}`}
                  className="common-card d-flex"
                >
                  <div
                    id={`eqc-card-index-${index}`}
                    className="mr-5 fw-medium"
                  >
                    <div className="d-flex align-center">{index + 1}.</div>
                  </div>
                  <div id={`eqc-card-details-${index}`}>
                    <div
                      id={`eqc-card-name-${index}`}
                      className="card-header fw-medium"
                    >
                      <span className="text-break">{eqcType?.name}</span>
                    </div>
                    <div
                      id={`eqc-card-content-details-${index}`}
                      className="card-content text-secondary"
                    >
                      <br />
                      <div id={`eqc-card-uom-${index}`} className="mb-15">
                        <span className="fw-medium mr-5">UOM:</span>
                        <span className="text-break">{UOMS[eqcType?.uom]}</span>
                      </div>
                      <div id={`eqc-card-ref-${index}`} className="mb-15 ">
                        <span className="fw-medium mr-5">
                          Reference Number:
                        </span>
                        <span className="text-break">{eqcType?.iso}</span>
                      </div>
                      <div id={`eqc-card-status-${index}`} className="mb-15 ">
                        <span className="fw-medium mr-5">Status:</span>
                        <ActiveStatus record={eqcType} />
                      </div>
                      <div
                        id={`eqc-card-updated-by-${index}`}
                        className="mb-15 "
                      >
                        <span className="fw-medium mr-5">Updated By:</span>
                        {eqcType?.creator?.name}
                      </div>
                      <div id={`eqc-card-updated-at-${index}`}>
                        <span className="fw-medium mr-5">Updated At:</span>
                        {eqcType?.updatedAt
                          ? moment(`${eqcType?.updatedAt}`).format(
                              DEFAULTDATETIMEFORMAT,
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <span
                    id={`eqc-card-actions-${index}`}
                    className="d-flex position-absolute user-action-btn"
                    onClick={(e) => e?.stopPropagation?.()}
                  >
                    {getActionButtons(eqcType, index)}
                  </span>
                </div>
              </CommonCard>
            );
          })}
        </InfiniteScrollHandler>
      )}
    </>
  );
};

export default EqcTable;
